import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Web as WebIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  OpenInNew as OpenIcon
} from '@mui/icons-material';
import { Agent } from '../../types/agent';
import { useTranslation } from 'react-i18next';

interface WebChatCardProps {
  agent: Agent;
}

const WebChatCard: React.FC<WebChatCardProps> = ({ agent }) => {
  const { t } = useTranslation();
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const webChatScript = `<!-- LIA TALK WebChat Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${window.location.origin}/webchat/widget.js';
    script.setAttribute('data-agent-id', '${agent.id}');
    script.setAttribute('data-agent-name', '${agent.name}');
    script.async = true;
    document.head.appendChild(script);
  })();
</script>`;

  const handleCopyScript = async () => {
    try {
      await navigator.clipboard.writeText(webChatScript);
      setSnackbarMessage(t('agents.webchat.scriptCopied'));
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Erro ao copiar script:', err);
      setSnackbarMessage(t('agents.webchat.copyError'));
      setSnackbarOpen(true);
    }
  };

  const handleOpenChat = () => {
    setChatDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WebIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('agents.webchat.title')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => setScriptDialogOpen(true)}
                size="small"
                disabled
              >
                {t('agents.webchat.script')}
              </Button>
              {/* <Button
                variant="contained"
                startIcon={<OpenIcon />}
                onClick={handleOpenChat}
                size="small"
              >
                Testar Chat
              </Button> */}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('agents.webchat.description', { name: agent.name })}
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('agents.webchat.comingSoon')}:</strong> {t('agents.webchat.comingSoonDescription')}
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Dialog para mostrar o script */}
      <Dialog
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('agents.webchat.scriptTitle')}</Typography>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={handleCopyScript}
              size="small"
            >
              {t('agents.webchat.copy')}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('agents.webchat.scriptInstructions')}
          </Typography>
          <TextField
            multiline
            rows={8}
            value={webChatScript}
            variant="outlined"
            fullWidth
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScriptDialogOpen(false)}>
            {t('agents.webchat.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para testar o chat */}
      <Dialog
        open={chatDialogOpen}
        onClose={() => setChatDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WebIcon color="primary" />
            <Typography variant="h6">{t('agents.webchat.testTitle', { name: agent.name })}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <WebChatWidget agent={agent} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatDialogOpen(false)}>
            {t('agents.webchat.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

// Componente do widget de chat para teste
const WebChatWidget: React.FC<{ agent: Agent }> = ({ agent }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{ id: number, text: string, isUser: boolean }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isUser: true
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simular resposta do agente
    setTimeout(() => {
      const responses = [
        `Olá! Sou o agente ${agent.name}. Como posso ajudá-lo hoje?`,
        `Entendo sua pergunta. Deixe-me verificar isso para você.`,
        `Ótima pergunta! Baseado nas informações que tenho, posso sugerir...`,
        `Posso ajudá-lo com mais alguma coisa?`,
        `Obrigado por entrar em contato! Estou aqui para ajudar.`
      ];

      const randomResponse = responses[Math.floor(Math.random() * responses.length)];

      const agentMessage = {
        id: Date.now() + 1,
        text: randomResponse,
        isUser: false
      };

      setMessages(prev => [...prev, agentMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
      {/* Header do chat */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer'
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <WebIcon />
        <Typography variant="h6">{agent.name}</Typography>
        <Box sx={{ ml: 'auto' }}>
          <Typography variant="body2">
            {isOpen ? '▼' : '▲'}
          </Typography>
        </Box>
      </Box>

      {/* Área de mensagens */}
      {isOpen && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0' }}>
          <Box sx={{ flex: 1, p: 2, overflowY: 'auto', maxHeight: '300px' }}>
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                {t('agents.webchat.startConversation', { name: agent.name })}
              </Typography>
            )}

            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    bgcolor: message.isUser ? 'primary.main' : 'grey.200',
                    color: message.isUser ? 'white' : 'text.primary',
                    p: 1.5,
                    borderRadius: 2,
                    maxWidth: '80%',
                    wordWrap: 'break-word'
                  }}
                >
                  <Typography variant="body2">
                    {message.text}
                  </Typography>
                </Box>
              </Box>
            ))}

            {isTyping && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <Box
                  sx={{
                    bgcolor: 'grey.200',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('agents.webchat.typing', { name: agent.name })}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ width: 4, height: 4, bgcolor: 'grey.400', borderRadius: '50%', animation: 'pulse 1.5s infinite' }} />
                    <Box sx={{ width: 4, height: 4, bgcolor: 'grey.400', borderRadius: '50%', animation: 'pulse 1.5s infinite 0.2s' }} />
                    <Box sx={{ width: 4, height: 4, bgcolor: 'grey.400', borderRadius: '50%', animation: 'pulse 1.5s infinite 0.4s' }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Input de mensagem */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder={t('agents.webchat.typeMessage')}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                size="small"
                disabled={isTyping}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                size="small"
              >
                {t('agents.webchat.send')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WebChatCard;
